import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { Link, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiSettings,
  FiList,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiCopy,
  FiCheck,
  FiX,
  FiClock,
  FiAward,
  FiEye,
  FiExternalLink,
  FiLayers,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";
import {
  getTimerSettings,
  updateTimerSettings,
  getTimerHistory,
} from "../api/timerGameApi";
import { getEvents } from "../api/eventApi";
import { formatUtcDateTimeWithSeconds } from "../utils/dateUtils";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return formatUtcDateTimeWithSeconds(dateStr);
  } catch {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? String(dateStr) : d.toLocaleString();
  }
};

const formatSecondsReadable = (secs) => {
  const s = Math.max(0, Number(secs) || 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
};

export default function TimerRewardGame() {
  const navigate = useNavigate();

  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/games");
    }
  };

  // Main Tabs: "settings" | "history"
  const [activeTab, setActiveTab] = useState("settings");

  // ==========================================
  // ⚙️ 1. Settings State
  // ==========================================
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  const [formSettings, setFormSettings] = useState({
    timerSeconds: 30,
    eventPoints: 50,
    cooldownSeconds: 0,
    isActive: true,
    name: "Timer Reward",
    description: "Wait for the countdown timer to finish and claim event points!",
  });

  // KPI Lifetime Stats
  const [lifetimeStats, setLifetimeStats] = useState({
    totalSessions: 0,
    completedSessions: 0,
    activeSessions: 0,
    totalPointsAwarded: 0,
  });

  // ==========================================
  // 📜 2. Audit History State
  // ==========================================
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    page: 1,
    perPage: 20,
  });
  const [historySummary, setHistorySummary] = useState({
    totalSessions: 0,
    completedSessions: 0,
    totalPointsAwarded: 0,
  });

  // Session Detail Modal
  const [selectedSession, setSelectedSession] = useState(null);
  const [copiedSessionId, setCopiedSessionId] = useState("");
  const searchDebounceTimer = useRef(null);

  // Events Selector
  const [eventsList, setEventsList] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("all");

  // Debounced search for audit history
  useEffect(() => {
    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => {
      if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
    };
  }, [search]);

  // Resolve active/selected event details
  const activeRunningEvent = useMemo(() => {
    if (!Array.isArray(eventsList)) return null;
    return eventsList.find((e) => e.status?.toLowerCase() === "running");
  }, [eventsList]);

  const selectedEventObj = useMemo(() => {
    if (!selectedEventId || selectedEventId === "all" || !Array.isArray(eventsList)) return null;
    return eventsList.find(
      (e) => String(e.eventId) === String(selectedEventId) || String(e.id) === String(selectedEventId)
    );
  }, [selectedEventId, eventsList]);

  const selectedEventName = selectedEventObj
    ? selectedEventObj.title || selectedEventObj.name || selectedEventObj.eventName || `Event #${selectedEventObj.eventId || selectedEventObj.id}`
    : selectedEventId === "all"
    ? "All Events"
    : "Active Event";

  // ==========================================
  // 🚀 Load Timer Settings & Lifetime Stats
  // ==========================================
  const fetchSettings = useCallback(async (showToast = false) => {
    setSettingsLoading(true);
    try {
      const res = await getTimerSettings();
      if (res?.status && res.data) {
        const d = res.data;
        setFormSettings({
          timerSeconds: Number(d.timerSeconds ?? 30),
          eventPoints: Number(d.eventPoints ?? 50),
          cooldownSeconds: Number(d.cooldownSeconds ?? 0),
          isActive: Boolean(d.isActive),
          name: d.name || "Timer Reward",
          description: d.description || "Wait for countdown timer to finish and claim event points!",
        });

        if (d.stats) {
          setLifetimeStats({
            totalSessions: Number(d.stats.totalSessions ?? 0),
            completedSessions: Number(d.stats.completedSessions ?? 0),
            activeSessions: Number(d.stats.activeSessions ?? 0),
            totalPointsAwarded: Number(d.stats.totalPointsAwarded ?? 0),
          });
        }
        if (showToast) toast.success("Timer settings reloaded!");
      }
    } catch (err) {
      console.warn("Could not load timer settings:", err.message);
      if (showToast) toast.error(err.message || "Failed to load timer settings");
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  // ==========================================
  // 📜 Load Audit History
  // ==========================================
  const fetchHistory = useCallback(
    async (showToast = false, eventOverride = null) => {
      setHistoryLoading(true);
      const activeEvt = eventOverride !== null ? eventOverride : selectedEventId;
      try {
        const res = await getTimerHistory({
          page,
          limit: perPage,
          search: debouncedSearch,
          status: statusFilter,
          eventId: activeEvt && activeEvt !== "all" ? activeEvt : undefined,
        });

        if (res?.status) {
          const list = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.history)
            ? res.data.history
            : [];
          setHistory(list);

          const paginationObj = res.pagination || res.data?.pagination || {};
          setPagination({
            total: Number(paginationObj.total ?? list.length),
            totalPages: Number(paginationObj.totalPages ?? Math.max(1, Math.ceil((paginationObj.total || list.length) / perPage))),
            page: Number(paginationObj.page ?? page),
            perPage: Number(paginationObj.limit ?? perPage),
          });

          if (res.summary) {
            setHistorySummary({
              totalSessions: Number(res.summary.totalSessions ?? 0),
              completedSessions: Number(res.summary.completedSessions ?? 0),
              totalPointsAwarded: Number(res.summary.totalPointsAwarded ?? 0),
            });
            // Update lifetime stats if available
            setLifetimeStats((prev) => ({
              ...prev,
              totalSessions: Number(res.summary.totalSessions ?? prev.totalSessions),
              completedSessions: Number(res.summary.completedSessions ?? prev.completedSessions),
              totalPointsAwarded: Number(res.summary.totalPointsAwarded ?? prev.totalPointsAwarded),
            }));
          }

          if (showToast) toast.success("Audit history updated!");
        } else {
          setHistory([]);
        }
      } catch (err) {
        console.error("Failed to load timer history:", err);
        setHistory([]);
        if (showToast) toast.error(err.message || "Failed to load history");
      } finally {
        setHistoryLoading(false);
      }
    },
    [page, perPage, debouncedSearch, statusFilter, selectedEventId]
  );

  // Initialize on mount
  useEffect(() => {
    async function init() {
      fetchSettings();

      try {
        const res = await getEvents({ limit: 100, page: 1, status: "all" });
        const data = res?.data || res;
        const list = data?.events || (Array.isArray(data) ? data : []);
        setEventsList(list);

        const running = list.find((e) => e.status?.toLowerCase() === "running");
        const defaultEvtId = running ? running.eventId || running.id : "all";
        setSelectedEventId(defaultEvtId);
        fetchHistory(false, defaultEvtId);
      } catch {
        fetchHistory(false, "all");
      }
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch history when search/status/page changes
  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory(false);
    }
  }, [activeTab, fetchHistory]);

  // ==========================================
  // 💾 Save Settings Handlers
  // ==========================================
  // Handler A: PUT /admin/timer/settings (Update Config)
  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();

    const tSec = Number(formSettings.timerSeconds);
    const pts = Number(formSettings.eventPoints);
    const cDown = Number(formSettings.cooldownSeconds);

    if (isNaN(tSec) || tSec < 1) {
      toast.error("Timer duration must be at least 1 second!");
      return;
    }
    if (isNaN(pts) || pts < 0) {
      toast.error("Event points cannot be negative!");
      return;
    }

    setSavingSettings(true);
    try {
      const res = await updateTimerSettings({
        timerSeconds: tSec,
        eventPoints: pts,
        cooldownSeconds: cDown,
        isActive: Boolean(formSettings.isActive),
      });

      if (res?.status) {
        toast.success(res.message || "Timer configuration updated successfully!");
        fetchSettings();
      } else {
        toast.error(res?.message || "Failed to update timer settings");
      }
    } catch (err) {
      toast.error(err.message || "Failed to save timer settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(String(text));
    setCopiedSessionId(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedSessionId(""), 2000);
  };

  const completionRate =
    lifetimeStats.totalSessions > 0
      ? Math.round((lifetimeStats.completedSessions / lifetimeStats.totalSessions) * 100)
      : 0;

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <Toaster position="top-right" />

      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        {/* Full-width container */}
        <div className="p-5 sm:p-7 space-y-6 w-full">
          {/* Breadcrumb Navigation & Back Button */}
          <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500">
            <button
              onClick={handleGoBack}
              className="flex items-center gap-1 hover:text-black transition cursor-pointer p-1 -ml-1 rounded hover:bg-gray-200"
              title="Go back"
            >
              <FiArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <Link to="/dashboard" className="hover:text-black transition">
              Dashboard
            </Link>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <Link to="/games" className="hover:text-black transition flex items-center gap-1">
              <span>Games Management</span>
            </Link>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <span className="text-emerald-700 font-bold">Timer Reward</span>
          </div>

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-2xl shadow-xs">
                ⏱️
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                    Timer Event Reward
                  </h1>
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase border ${
                      formSettings.isActive
                        ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                        : "bg-rose-100 text-rose-800 border-rose-300"
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        formSettings.isActive ? "bg-emerald-500 animate-pulse" : "bg-rose-500"
                      }`}
                    />
                    {formSettings.isActive ? "Live" : "Paused"}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Configure countdown wait duration, event point rewards, cooldown intervals, and audit user claims.
                </p>
              </div>
            </div>

            {/* Top Action Button */}
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => {
                  fetchSettings(true);
                  if (activeTab === "history") fetchHistory(true);
                }}
                disabled={settingsLoading || historyLoading}
                className="flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl transition shadow-2xs cursor-pointer"
              >
                <FiRefreshCw
                  size={13}
                  className={`h-3.5 w-3.5 ${
                    settingsLoading || historyLoading ? "animate-spin text-emerald-600" : ""
                  }`}
                />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Top 4 KPI Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
            {/* 1. Total Sessions */}
            <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-4 flex flex-col justify-between hover:border-gray-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  Total Sessions
                </span>
                <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <FiClock size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-gray-900 font-mono mt-1.5">
                {(lifetimeStats.totalSessions || historySummary.totalSessions || 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-400 mt-1.5 font-medium">Lifetime user sessions</p>
            </div>

            {/* 2. Completed Claims */}
            <div className="bg-white rounded-2xl shadow-xs border border-emerald-200/80 bg-gradient-to-br from-white to-emerald-50/20 p-4 flex flex-col justify-between hover:border-emerald-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">
                  Completed Claims
                </span>
                <div className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                  <FiCheckCircle size={13} />
                </div>
              </div>
              <div className="flex items-baseline gap-2 mt-1.5">
                <p className="text-2xl font-black text-emerald-700 font-mono">
                  {(lifetimeStats.completedSessions || historySummary.completedSessions || 0).toLocaleString()}
                </p>
                <span className="text-[11px] font-bold text-emerald-600 bg-emerald-100/70 px-1.5 py-0.5 rounded">
                  {completionRate}% rate
                </span>
              </div>
              <p className="text-[11px] text-emerald-700/80 mt-1.5 font-medium">Fully waited & claimed</p>
            </div>

            {/* 3. Active Sessions */}
            <div className="bg-white rounded-2xl shadow-xs border border-amber-200/80 bg-gradient-to-br from-white to-amber-50/20 p-4 flex flex-col justify-between hover:border-amber-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-amber-900 uppercase tracking-wider">
                  Active Waiting
                </span>
                <div className="w-6 h-6 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center font-bold">
                  <FiLayers size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-amber-900 font-mono mt-1.5">
                {(lifetimeStats.activeSessions || 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-amber-800/80 mt-1.5 font-medium">Currently ticking down</p>
            </div>

            {/* 4. Total Event Points Awarded */}
            <div className="bg-white rounded-2xl shadow-xs border border-purple-200/80 bg-gradient-to-br from-white to-purple-50/20 p-4 flex flex-col justify-between hover:border-purple-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-purple-800 uppercase tracking-wider">
                  Points Credited
                </span>
                <div className="w-6 h-6 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
                  <FiAward size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-purple-700 font-mono mt-1.5">
                +{(lifetimeStats.totalPointsAwarded || historySummary.totalPointsAwarded || 0).toLocaleString()}{" "}
                <span className="text-[11px] font-sans text-purple-600 font-bold">PTS</span>
              </p>
              <p className="text-[11px] text-purple-700/80 mt-1.5 font-medium">To user event points</p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 bg-white border border-gray-200 rounded-xl p-1 w-fit shadow-xs">
            <button
              type="button"
              onClick={() => setActiveTab("settings")}
              className={`px-4 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1.5 cursor-pointer ${
                activeTab === "settings"
                  ? "bg-[#ffff00] text-black shadow-2xs"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <FiSettings size={13} />
              <span>Timer Configuration</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className={`px-4 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1.5 cursor-pointer ${
                activeTab === "history"
                  ? "bg-[#ffff00] text-black shadow-2xs"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <FiList size={13} />
              <span>Session Audit History</span>
              {history.length > 0 && (
                <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-black/10 font-mono">
                  {pagination.total || history.length}
                </span>
              )}
            </button>
          </div>

          {/* ========================================================================= */}
          {/* TAB 1: ⚙️ TIMER SETTINGS */}
          {/* ========================================================================= */}
          {activeTab === "settings" && (
            <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-6 sm:p-7 space-y-6">
              {settingsLoading ? (
                <div className="py-16 flex flex-col items-center justify-center gap-3 text-gray-400">
                  <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs font-semibold text-gray-500">Loading timer settings...</p>
                </div>
              ) : (
                <form onSubmit={handleSaveSettings} className="space-y-6">
                  {/* Status Toggle Card */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div>
                      <label className="text-sm font-bold text-gray-800">
                        Timer Reward Status
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Enable or disable the countdown timer reward in the mobile app.
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <span
                        className={`text-xs font-bold ${
                          formSettings.isActive ? "text-emerald-700" : "text-gray-500"
                        }`}
                      >
                        {formSettings.isActive ? "Active (Live)" : "Disabled"}
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formSettings.isActive}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, isActive: e.target.checked }))
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ffff00]"></div>
                      </label>
                    </div>
                  </div>

                  {/* 3 Core Fields Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {/* 1. Timer Duration */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Timer Duration (Seconds) <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min={1}
                          max={86400}
                          required
                          value={formSettings.timerSeconds}
                          onChange={(e) =>
                            setFormSettings((prev) => ({
                              ...prev,
                              timerSeconds: Math.max(1, Number(e.target.value)),
                            }))
                          }
                          className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition font-mono"
                          placeholder="e.g. 30"
                        />
                        <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">
                          sec
                        </span>
                      </div>
                      <span className="text-[11px] text-gray-400 mt-1 block">
                        Wait time required: {formatSecondsReadable(formSettings.timerSeconds)}
                      </span>
                    </div>

                    {/* 2. Event Points Awarded */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Event Points Awarded <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min={0}
                          max={1000000}
                          required
                          value={formSettings.eventPoints}
                          onChange={(e) =>
                            setFormSettings((prev) => ({
                              ...prev,
                              eventPoints: Math.max(0, Number(e.target.value)),
                            }))
                          }
                          className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition font-mono"
                          placeholder="e.g. 50"
                        />
                        <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">
                          PTS
                        </span>
                      </div>
                      <span className="text-[11px] text-gray-400 mt-1 block">
                        Points credited to active event leaderboard
                      </span>
                    </div>

                    {/* 3. Cooldown Interval */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Cooldown Interval (Seconds)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min={0}
                          max={86400}
                          value={formSettings.cooldownSeconds}
                          onChange={(e) =>
                            setFormSettings((prev) => ({
                              ...prev,
                              cooldownSeconds: Math.max(0, Number(e.target.value)),
                            }))
                          }
                          className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition font-mono"
                          placeholder="e.g. 0"
                        />
                        <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">
                          sec
                        </span>
                      </div>
                      <span className="text-[11px] text-gray-400 mt-1 block">
                        {Number(formSettings.cooldownSeconds) === 0
                          ? "0s (Instant replay, no cooldown)"
                          : `${formatSecondsReadable(formSettings.cooldownSeconds)} break between sessions`}
                      </span>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="pt-4 border-t border-gray-100 flex items-center justify-end">
                    <button
                      type="submit"
                      disabled={savingSettings}
                      className="px-6 py-2.5 bg-[#ffff00] hover:bg-[#e6e600] text-black font-bold text-xs rounded-xl shadow-xs transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {savingSettings ? (
                        <>
                          <FiRefreshCw className="h-3.5 w-3.5 animate-spin" />
                          <span>Saving Settings...</span>
                        </>
                      ) : (
                        <>
                          <FiSave className="h-3.5 w-3.5" />
                          <span>Save Settings</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* ========================================================================= */}
          {/* TAB 2: 📜 SESSION AUDIT HISTORY LOGS */}
          {/* ========================================================================= */}
          {activeTab === "history" && (
            <div className="space-y-4">
              {/* Search & Filter Toolbar */}
              <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-1 max-w-xl">
                  {/* Search Input */}
                  <div className="relative flex-1">
                    <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5" />
                    <input
                      type="text"
                      placeholder="Search username, email, session ID..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:bg-white transition"
                    />
                  </div>

                  {/* Status Filter */}
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    className="bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700 px-3 py-2 focus:outline-none focus:border-yellow-400 font-semibold"
                  >
                    <option value="all">All Statuses</option>
                    <option value="completed">Completed</option>
                    <option value="started">Started / Active</option>
                    <option value="disqualified">Disqualified</option>
                    <option value="expired">Expired</option>
                  </select>

                  {/* Event Filter */}
                  <select
                    value={selectedEventId}
                    onChange={(e) => {
                      setSelectedEventId(e.target.value);
                      setPage(1);
                    }}
                    className="bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700 px-3 py-2 focus:outline-none focus:border-yellow-400 font-semibold max-w-[180px] truncate"
                  >
                    <option value="all">All Events</option>
                    {eventsList.map((ev) => (
                      <option key={ev.eventId || ev.id} value={ev.eventId || ev.id}>
                        {ev.status === "running" ? "🟢 " : "⚪ "}
                        {ev.eventName || ev.title || `Event #${ev.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2 justify-end">
                  <span className="text-xs text-gray-500">
                    Showing <strong>{history.length}</strong> of{" "}
                    <strong>{pagination.total || history.length}</strong> sessions
                  </span>
                  <button
                    onClick={() => fetchHistory(true)}
                    disabled={historyLoading}
                    className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600 transition cursor-pointer"
                    title="Refresh logs"
                  >
                    <FiRefreshCw size={13} className={historyLoading ? "animate-spin text-emerald-600" : ""} />
                  </button>
                </div>
              </div>

              {/* Audit Table */}
              <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
                {historyLoading ? (
                  <div className="p-16 text-center">
                    <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-gray-500 text-xs font-semibold">Loading timer session logs...</p>
                  </div>
                ) : history.length === 0 ? (
                  <div className="p-16 text-center">
                    <div className="w-12 h-12 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center mx-auto mb-3 text-xl">
                      ⏱️
                    </div>
                    <h3 className="text-sm font-bold text-gray-800">No Timer Sessions Found</h3>
                    <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
                      No user timer sessions match the current search or status filter. Users will appear here once they start countdowns in the mobile app.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50/80 border-b border-gray-200 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                          <th className="py-3 px-4">Session ID</th>
                          <th className="py-3 px-4">User</th>
                          <th className="py-3 px-4">Event</th>
                          <th className="py-3 px-4 text-center">Target Time</th>
                          <th className="py-3 px-4 text-center">Elapsed</th>
                          <th className="py-3 px-4 text-right">Points Awarded</th>
                          <th className="py-3 px-4 text-center">Status</th>
                          <th className="py-3 px-4">Started / Completed</th>
                          <th className="py-3 px-4 text-center">Inspect</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-xs">
                        {history.map((row, idx) => {
                          const sid = row.sessionId || row.session_id || row.id;
                          const shortSid = sid ? `${String(sid).substring(0, 12)}...` : "—";
                          const isCompleted = row.status === "completed" || row.status === "Claimed";
                          const isStarted = row.status === "started";
                          const isDisqualified = row.status === "disqualified";

                          const timing = row.timing ?? row.timerSeconds ?? 30;
                          const elapsed = row.elapsedSeconds ?? row.elapsed ?? 0;
                          const claimedPts = row.claimedPoints ?? row.eventPoints ?? 0;

                          const userId = row.userId || row.user_id || row.user?.id;
                          const userName =
                            row.username ||
                            row.userName ||
                            row.user?.username ||
                            row.user?.email ||
                            `User #${userId || "—"}`;

                          return (
                            <tr
                              key={sid || idx}
                              className="hover:bg-gray-50/80 transition duration-150"
                            >
                              {/* Session ID */}
                              <td className="py-3 px-4 whitespace-nowrap font-mono text-gray-700">
                                <div className="flex items-center gap-1.5">
                                  <span>{shortSid}</span>
                                  {sid && (
                                    <button
                                      onClick={() => copyToClipboard(sid, sid)}
                                      className="text-gray-400 hover:text-black p-1 rounded transition cursor-pointer"
                                      title="Copy full session ID"
                                    >
                                      {copiedSessionId === sid ? (
                                        <FiCheck className="h-3 w-3 text-emerald-600" />
                                      ) : (
                                        <FiCopy className="h-3 w-3" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </td>

                              {/* User Info */}
                              <td className="py-3 px-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-xs uppercase">
                                    {userName.charAt(0)}
                                  </div>
                                  <div>
                                    {userId ? (
                                      <Link
                                        to={`/user/${userId}`}
                                        className="font-bold text-gray-900 hover:text-emerald-700 transition block hover:underline"
                                      >
                                        {userName}
                                      </Link>
                                    ) : (
                                      <span className="font-bold text-gray-800 block">{userName}</span>
                                    )}
                                    {row.email && (
                                      <span className="text-[10px] text-gray-400 block truncate max-w-[150px]">
                                        {row.email}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>

                              {/* Event Name */}
                              <td className="py-3 px-4 whitespace-nowrap">
                                <span className="font-semibold text-gray-800 block truncate max-w-[140px]" title={row.eventName || row.eventId}>
                                  {row.eventName || row.eventId || "Global Event"}
                                </span>
                              </td>

                              {/* Target Timing */}
                              <td className="py-3 px-4 whitespace-nowrap text-center font-mono font-bold text-gray-700">
                                {timing}s
                              </td>

                              {/* Elapsed Time vs Required */}
                              <td className="py-3 px-4 whitespace-nowrap text-center font-mono">
                                <span
                                  className={`font-bold ${
                                    elapsed >= timing
                                      ? "text-emerald-700"
                                      : isCompleted
                                      ? "text-gray-700"
                                      : "text-amber-600"
                                  }`}
                                >
                                  {elapsed}s
                                </span>
                                {elapsed >= timing && (
                                  <span className="ml-1 text-[10px] text-emerald-600 font-sans font-bold">✓</span>
                                )}
                              </td>

                              {/* Points Awarded */}
                              <td className="py-3 px-4 whitespace-nowrap text-right font-mono font-extrabold text-purple-700">
                                +{Number(claimedPts).toLocaleString()} PTS
                              </td>

                              {/* Status Badge */}
                              <td className="py-3 px-4 whitespace-nowrap text-center">
                                <span
                                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 ${
                                    isCompleted
                                      ? "bg-emerald-50 text-emerald-700 border border-emerald-300"
                                      : isStarted
                                      ? "bg-blue-50 text-blue-700 border border-blue-300"
                                      : isDisqualified
                                      ? "bg-rose-50 text-rose-700 border border-rose-300"
                                      : "bg-gray-100 text-gray-600 border border-gray-200"
                                  }`}
                                >
                                  {isCompleted ? "✓ Completed" : row.status || "started"}
                                </span>
                              </td>

                              {/* Started / Completed Timestamps */}
                              <td className="py-3 px-4 whitespace-nowrap text-gray-500 text-[11px]">
                                <div>
                                  <span className="text-gray-400">Start:</span>{" "}
                                  {formatDateTime(row.startedAt || row.createdAt)}
                                </div>
                                {row.completedAt && (
                                  <div className="text-[10px] text-emerald-700">
                                    <span className="text-gray-400">End:</span>{" "}
                                    {formatDateTime(row.completedAt)}
                                  </div>
                                )}
                              </td>

                              {/* Inspect Detail Modal */}
                              <td className="py-3 px-4 whitespace-nowrap text-center">
                                <button
                                  onClick={() => setSelectedSession(row)}
                                  className="p-1.5 rounded-lg bg-gray-100 hover:bg-emerald-100 text-gray-600 hover:text-emerald-800 transition cursor-pointer"
                                  title="Inspect full session details"
                                >
                                  <FiEye size={13} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Table Footer with Pagination */}
                {history.length > 0 && (
                  <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
                    <div className="flex items-center gap-3">
                      <span>
                        Showing Page <strong>{pagination.page}</strong> of{" "}
                        <strong>{pagination.totalPages}</strong> ({pagination.total} total sessions)
                      </span>
                      <div className="flex items-center gap-1.5">
                        <span>Per page:</span>
                        <select
                          value={perPage}
                          onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setPage(1);
                          }}
                          className="bg-white border border-gray-200 rounded-lg px-2 py-0.5 text-xs text-gray-700 focus:outline-none"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1 || historyLoading}
                        className="px-3 py-1 bg-white border border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs"
                      >
                        Prev
                      </button>
                      <span className="font-bold text-gray-800">
                        Page {page} of {pagination.totalPages}
                      </span>
                      <button
                        onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                        disabled={page >= pagination.totalPages || historyLoading}
                        className="px-3 py-1 bg-white border border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 🔍 SESSION DETAIL INSPECTION MODAL */}
      {/* ========================================================================= */}
      {selectedSession && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl border border-gray-200 max-w-lg w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center text-sm">
                  ⏱️
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Timer Session Details</h3>
                  <span className="text-[11px] text-gray-400 font-mono">
                    {selectedSession.sessionId || selectedSession.session_id || "Session"}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedSession(null)}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition cursor-pointer"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              {/* User details */}
              <div className="p-3 rounded-xl bg-gray-50 border border-gray-200/80 flex items-center justify-between">
                <div>
                  <span className="text-[11px] text-gray-400 block font-medium">User</span>
                  <span className="font-bold text-gray-900 block">
                    {selectedSession.username || selectedSession.userName || `User #${selectedSession.userId}`}
                  </span>
                  {selectedSession.email && (
                    <span className="text-[11px] text-gray-500 block">{selectedSession.email}</span>
                  )}
                </div>
                {selectedSession.userId && (
                  <Link
                    to={`/user/${selectedSession.userId}`}
                    className="px-2.5 py-1 rounded-lg bg-white border border-gray-200 hover:bg-[#ffff00] text-gray-700 hover:text-black font-bold text-[11px] transition flex items-center gap-1"
                  >
                    <span>View Profile</span>
                    <FiExternalLink size={11} />
                  </Link>
                )}
              </div>

              {/* Event & Status */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200/80">
                  <span className="text-[11px] text-gray-400 block font-medium">Event</span>
                  <span className="font-bold text-gray-900 block truncate">
                    {selectedSession.eventName || selectedSession.eventId || "Global Event"}
                  </span>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200/80">
                  <span className="text-[11px] text-gray-400 block font-medium">Status</span>
                  <span className="font-bold text-emerald-700 uppercase block">
                    {selectedSession.status || "started"}
                  </span>
                </div>
              </div>

              {/* Timing & Claim Details */}
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200/80">
                  <span className="text-[11px] text-gray-400 block font-medium">Required</span>
                  <span className="font-mono font-bold text-gray-900 text-sm mt-0.5 block">
                    {selectedSession.timing ?? selectedSession.timerSeconds ?? 30}s
                  </span>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200/80">
                  <span className="text-[11px] text-gray-400 block font-medium">Elapsed</span>
                  <span className="font-mono font-bold text-emerald-700 text-sm mt-0.5 block">
                    {selectedSession.elapsedSeconds ?? selectedSession.elapsed ?? 0}s
                  </span>
                </div>
                <div className="p-3 rounded-xl bg-purple-50 border border-purple-200/80">
                  <span className="text-[11px] text-purple-700 block font-medium">Claimed</span>
                  <span className="font-mono font-black text-purple-800 text-sm mt-0.5 block">
                    +{selectedSession.claimedPoints ?? selectedSession.eventPoints ?? 0} PTS
                  </span>
                </div>
              </div>

              {/* Timestamps */}
              <div className="p-3 rounded-xl bg-gray-50 border border-gray-200/80 space-y-1.5 font-mono text-[11px]">
                <div className="flex justify-between">
                  <span className="text-gray-400">Started At:</span>
                  <span className="font-semibold text-gray-800">
                    {formatDateTime(selectedSession.startedAt || selectedSession.createdAt)}
                  </span>
                </div>
                {selectedSession.completedAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Completed At:</span>
                    <span className="font-semibold text-emerald-700">
                      {formatDateTime(selectedSession.completedAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                onClick={() => setSelectedSession(null)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs rounded-xl transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
